<template>
    <div
        class="dk-color-picker tw-flex tw-flex-col tw-items-center"
        :class="{ 'lg:tw-items-start': isThreeStepsDkPromo }"
    >
        <p v-if="showColorName" class="dk-color-picker__color-name body-md tw-mb-6">
            {{ currentColorName }}
        </p>
        <div
            class="dk-color-picker__thumbs tw-flex tw-justify-center tw-flex-wrap"
            :class="thumbsClass"
            :style="parentWidth"
        >
            <dk-color-thumb
                v-for="(color, index) in colorsToShow"
                :key="`thumb-${index}`"
                :class="thumbWidthClass"
                :color="color"
                :size="size"
                :is-selected="index === currentSelection"
                @select-color="updateCurrentSelection(index)"
            />
            <dk-color-thumb
                v-if="showMore"
                class="dk-color-picker__show-more"
                :class="thumbWidthClass"
                :size="size"
                :show-more="showMore"
                :is-three-steps-dk-promo="isThreeStepsDkPromo || hasDealerSelectionLayout"
                @show-more="showAllColors"
            />
            <dk-color-thumb
                v-if="(isThreeStepsDkPromo || hasDealerSelectionLayout) && showLess"
                class="dk-color-picker__show-less"
                :class="thumbWidthClass"
                :size="size"
                :show-less="showLess"
                :is-three-steps-dk-promo="isThreeStepsDkPromo || hasDealerSelectionLayout"
                @show-less="showLessColors"
            />
        </div>
        <p v-if="disclaimer" class="dk-color-picker__disclaimer tw-mt-4 micro tw-text-base-subtle">
            {{ disclaimer }}
        </p>
    </div>
</template>

<script>
import DkColorThumb from '@v4/common/components/atoms/DkColorThumb.vue';
import { EventBus } from '@v4/common/utils/event-bus';

export default {
    name: 'DkColorPicker',
    components: {
        DkColorThumb,
    },
    props: {
        showColorName: {
            type: Boolean,
            default: true,
        },
        /**
         * Array of objects of type:
         * active:true
         * baseColourHex:"#000000"
         * basePrice:614.75
         * colorImage:Object
         * customDescription:null
         * description:"Solid Black (pastello)"
         * equipmentId:"422950"
         * finalPrice:"€ 750"
         * group:"Vernice pastello Solid Black"
         * manufacturerCode:"601"
         * msrpPrice:750
         * order:1
         * primaryHex:"#50483d"
         * secondaryHex:null
         */
        colors: {
            type: Array,
            required: true,
        },
        /**
         * Max number of initial colors to display, including 'show more'.
         */
        maxLanding: {
            type: Number,
            default: 5,
        },
        /**
         * Max number of colors per line after clicking on 'show more'.
         * Normally, it's 10 for desktop and 5 for mobile.
         */
        maxPerLine: {
            type: Number,
            default: 10,
        },
        /**
         * Available sizes: 's', 'l'.
         */
        size: {
            type: String,
            default: '',
        },
        /**
         * The disclaimer to show under the color picker.
         */
        disclaimer: {
            type: String,
            default: '',
        },
        /**
         * A flag to set to true when we want to show all available colors.
         */
        showAll: {
            type: Boolean,
            default: false,
        },
        /*
         * A flag for the custom classes for the 3-steps DkPromo page.
         */
        isThreeStepsDkPromo: {
            type: Boolean,
            default: false,
        },
        /*
         * A flag for the custom classes for the pages
         * that has dealer selection with a map.
         */
        hasDealerSelectionLayout: {
            type: Boolean,
            default: false,
        },
        isInsideModal: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            currentSelection: 0,
            colorsToShow: [],
            colorsToHide: [],
            showLess: false,
        };
    },
    computed: {
        showMore() {
            return this.showAll ? !this.showAll : this.colorsToHide.length > 0;
        },
        currentColorName() {
            return this.colors[this.currentSelection].description;
        },
        parentGapSize() {
            return this.size === 's' ? 'tw-gap-2' : 'tw-gap-4';
        },
        thumbWidthClass() {
            const thumbWidth = 100 / this.maxPerLine;
            return `tw-w-[${thumbWidth}%]`;
        },
        parentWidth() {
            const thumbWidthOnDesktop = 72;
            const thumbWidthOnMobile = 56;
            const gapOnDesktop = 16;
            const gapOnMobile = 8;

            const thumbWidth =
                this.size === 's' ? thumbWidthOnMobile * this.maxPerLine : thumbWidthOnDesktop * this.maxPerLine;
            const gapWith =
                this.size === 's' ? gapOnMobile * (this.maxPerLine - 1) : gapOnDesktop * (this.maxPerLine - 1);
            return { maxWidth: `${thumbWidth + gapWith}px` };
        },
        thumbsClass() {
            return {
                ...this.parentGapSize,
                'max-lg:!tw-max-w-card-mobile': this.hasDealerSelectionLayout,
                'lg:tw-justify-start': this.isThreeStepsDkPromo,
                'tw-gap-2': this.isThreeStepsDkPromo || this.hasDealerSelectionLayout,
                'lg:tw-gap-3':
                    !this.isInsideModal &&
                    ((this.isThreeStepsDkPromo && this.maxLanding <= 6) || this.hasDealerSelectionLayout),
                'lg:tw-gap-4':
                    !this.isInsideModal &&
                    ((this.isThreeStepsDkPromo && this.maxLanding > 6) || this.hasDealerSelectionLayout),
            };
        },
    },
    watch: {
        maxLanding(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.initializeColors();
            }
        },
    },
    mounted() {
        this.initializeColors();
        if (this.isInsideModal) {
            this.checkActiveColor();
        }
    },
    methods: {
        initializeColors() {
            const hasColorsToHide = this.showAll ? false : this.colors.length > this.maxLanding;
            this.colorsToShow = hasColorsToHide ? this.colors.slice(0, this.maxLanding - 1) : this.colors;
            this.colorsToHide = hasColorsToHide ? this.colors.slice(this.colorsToShow.length) : [];
        },
        updateCurrentSelection(index) {
            if (this.currentSelection !== index) {
                this.currentSelection = index;
                this.$emit('item-click', index);
            }
        },
        showAllColors() {
            this.colorsToShow = [...this.colorsToShow, ...this.colorsToHide];
            this.colorsToHide = [];
            if (this.isThreeStepsDkPromo || this.hasDealerSelectionLayout) {
                this.toggleShowLess();
                EventBus.$emit('DkColorPicker:show-less-toggle', this.showLess);
            }
        },
        showLessColors() {
            this.colorsToShow = this.colors.slice(0, this.maxLanding - 1);
            this.colorsToHide = this.colors.slice(this.colorsToShow.length);
            this.toggleShowLess();
            if (this.currentSelection > this.maxLanding - 2) {
                const currentColorIndex = this.colorsToHide.findIndex((color) => color.active === true);
                this.reorganizeColorsOrder(currentColorIndex);
                this.currentSelection = 0;
            }
            EventBus.$emit('DkColorPicker:show-less-toggle', this.showLess);
        },
        reorganizeColorsOrder(index) {
            // Move the color from colorsToHide to colorsToShow
            this.colorsToShow.unshift({ ...this.colorsToHide.splice(index, 1)[0] });

            // Move the last color of colorsToShow to the start of colorsToHide
            this.colorsToHide.unshift({ ...this.colorsToShow.pop() });

            const colorIndex = this.colors.findIndex((color) => color.active === true);
            if (colorIndex !== -1) {
                this.$emit('order-change', colorIndex);
            }
        },
        checkActiveColor() {
            const index = this.colors.findIndex((color) => color.active === true);
            if (index !== -1 && index !== this.currentSelection) {
                this.updateCurrentSelection(index);
            }
        },
        toggleShowLess() {
            this.showLess = !this.showLess;
            this.$emit('show-less', this.showLess);
        },
    },
};
</script>
